import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import moment from 'moment';

import ReactTable from 'react-table';
import {
  Button,
  Row,
  ControlLabel,
  Col,
  ButtonToolbar,
} from 'react-bootstrap';
import { queryGetAllAndApprovedPerDiemsGQL } from '../graphql/getAllPerDiemsApi';
import {
  denyPerDiemModalPropTypes,
  withDenyPerDiemModalState,
  DenyPerDiemModal,
} from './modals/DenyPerDiemModal';

import {
  ApprovePerDiemModal,
  withApprovePerDiemModalState,
  approvePerDiemModalPropTypes,
} from './modals/ApprovePerDiem';

import {
  defaultTableFiltering,
  openInNewTab,
  dateTableSorting,
} from '../../../utils';

import { renderTicketNumber, getTicketState } from '../../tickets/utils';
import { getUserTimesheetLink } from '../../timesheetNavigator/links';

import DistrictFilter from '../../fields/DistrictFilter';
import { approvePerDiemRequest, denyPerDiemRequest, setDistrict, setPerDiemId } from '../actions';
import { getDistrictId, getPerDiemState } from '../selectors';


const propTypes = exact({
  allPerDiems: PropTypes.array.isRequired,
  districtId: PropTypes.number,
  setDistrictAction: PropTypes.func.isRequired,
  denyPerDiemAction: PropTypes.func.isRequired,
  approvePerDiemAction: PropTypes.func.isRequired,
  setPerDiemIdAction: PropTypes.func.isRequired,
  perDiemState: PropTypes.string,

  ...denyPerDiemModalPropTypes,
  ...approvePerDiemModalPropTypes,
});


const PerDiemApprovals = (props) => {
  const {
    openDenyModal, closeDenyModal, showDenyModal,
  } = props;

  const {
    openApproveModal, closeApproveModal, showApproveModal,
  } = props;

  const {
    allPerDiems,
    setDistrictAction,
    districtId,
    setPerDiemIdAction,
    denyPerDiemAction,
    approvePerDiemAction,
  } = props;

  return (
    <React.Fragment>
      <Row style={{ paddingBottom: '20px' }}>
        <Col md={2}>
          <ControlLabel>District</ControlLabel>
          <DistrictFilter
            name="Field District"
            value={{ id: districtId }}
            onChange={setDistrictAction}
          />
        </Col>
      </Row>
      <ReactTable
        filterable
        data={allPerDiems}
        columns={[
          {
            Header: 'Requested For Date',
            id: 'requested_for_date',
            sortMethod: (a, b) => {
              return dateTableSorting(a, b);
            },
            accessor: (e) => { return e.per_diem_date.display; },
          },
          {
            Header: 'Requested By',
            id: 'fullname',
            accessor: (e) => { return e.requested_by.fullname; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return defaultTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'For Employee',
            id: 'for_employee',
            accessor: (e) => { return e.user.fullname; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return defaultTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'Ticket Number',
            id: 'ticket_number',
            accessor: (e) => { return e.per_diem_request.ticket_number.number; },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return defaultTableFiltering(customFilterObj, row);
            },
            Cell: (row) => {
              const ticketNumber = row.original.per_diem_request.ticket_number;
              return (
                <div style={{ padding: '7px 5px', color: '#000' }} className={`summary-details-${getTicketState(ticketNumber)}`}>
                  {renderTicketNumber(ticketNumber)}
                </div>
              );
            },
          },
          {
            Header: 'Ticket Number Also Paid On',
            id: 'ticket_number_also_paid_on',
            filterable: false,
            sortable: false,
            Cell: (row) => {
              const dates = row.original.approved_per_diems.map((pd) => {
                return pd.per_diem_date.display;
              });
              return (
                <div>
                  { dates.join(', ') }
                </div>
              );
            },
          },
          {
            Header: 'Actions',
            id: 'actions',
            filterable: false,
            sortable: false,
            Cell: (row) => {
              const { user, per_diem_date: perDiemDate } = row.original;
              /* NOTE: centering the buttons in the actions column by using float none
              * and overriding buttonToolbar. ButtonToolbar adds the spaces between the buttons. */
              return (
                <ButtonToolbar>
                  <Button
                    style={{ float: 'none' }}
                    bsStyle="danger"
                    bsSize="small"
                    onClick={() => {
                      setPerDiemIdAction(row.original.id);
                      return openDenyModal();
                    }}
                  >Deny
                  </Button>
                  <Button
                    style={{ float: 'none' }}
                    bsStyle="primary"
                    bsSize="small"
                    onClick={() => {
                      setPerDiemIdAction(row.original.id);
                      return openApproveModal();
                    }}
                  >Approve
                  </Button>
                  <Button
                    style={{ float: 'none' }}
                    bsStyle="info"
                    bsSize="small"
                    onClick={() => {
                      return (
                        openInNewTab(getUserTimesheetLink(user.id, moment(perDiemDate.iso8601)))
                      );
                    }}
                  >View Timesheet
                  </Button>
                </ButtonToolbar>
              );
            },
          },
        ]}
      />
      <DenyPerDiemModal
        show={showDenyModal}
        onHide={closeDenyModal}
        denyPerDiemAction={denyPerDiemAction}
      />
      <ApprovePerDiemModal
        show={showApproveModal}
        onHide={closeApproveModal}
        approvePerDiemAction={approvePerDiemAction}
      />
    </React.Fragment>
  );
};

PerDiemApprovals.propTypes = propTypes;

const mapStateToProps = (state) => {
  return {
    districtId: getDistrictId(state),
    perDiemState: getPerDiemState(state),
  };
};

const mapDispatchToProps = {
  setDistrictAction: setDistrict,
  setPerDiemIdAction: setPerDiemId,
  denyPerDiemAction: denyPerDiemRequest,
  approvePerDiemAction: approvePerDiemRequest,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const enhance = compose(
  withRedux,
  queryGetAllAndApprovedPerDiemsGQL,
  withDenyPerDiemModalState,
  withApprovePerDiemModalState,
);


export default enhance(PerDiemApprovals);
